import React, { useState, useEffect } from 'react'
import { Bell, Mail, MessageSquare, Check, CheckCheck, Trash2, X } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { getUserNotifications, markNotificationAsRead, supabase } from '../lib/supabase'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'

interface Notification {
  id: string
  type: 'event_announcement' | 'order_confirmation' | 'order_status' | 'transfer_received' | 'system'
  title: string
  message: string
  channels: ('sms' | 'email' | 'push')[]
  read_at?: string
  created_at: string
}

const NotificationsPage: React.FC = () => {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  // Load notifications
  useEffect(() => {
    if (!user) return

    loadNotifications()
  }, [user])

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!user) return

    const subscription = supabase
      .channel(`notifications:${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'secret_notifications',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        const newNotification = payload.new as Notification
        setNotifications(prev => [newNotification, ...prev])
        toast.custom((t) => (
          <EnterpriseToast
            title={newNotification.title}
            message={newNotification.message}
            type="success"
            onDismiss={() => toast.dismiss(t.id)}
          />
        ), { duration: 3000 })
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'secret_notifications',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        const updatedNotification = payload.new as Notification
        setNotifications(prev =>
          prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
        )
      })
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [user])

  const loadNotifications = async () => {
    try {
      setIsLoading(true)
      const data = await getUserNotifications(user!.id)
      setNotifications(data)
    } catch (error) {
      console.error('Failed to load notifications:', error)
      toast.custom((t) => (
        <EnterpriseToast
          title="Loading Failed"
          message="Could not load your notifications"
          type="error"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    } finally {
      setIsLoading(false)
    }
  }

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId)
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, read_at: new Date().toISOString() }
            : n
        )
      )
    } catch (error) {
      console.error('Failed to mark as read:', error)
      toast.custom((t) => (
        <EnterpriseToast
          title="Update Failed"
          message="Could not mark notification as read"
          type="error"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      const unreadIds = notifications
        .filter(n => !n.read_at)
        .map(n => n.id)

      await Promise.all(
        unreadIds.map(id => markNotificationAsRead(id))
      )

      setNotifications(prev =>
        prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      )

      toast.custom((t) => (
        <EnterpriseToast
          title="All Cleared"
          message="All notifications have been marked as read"
          type="success"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    } catch (error) {
      console.error('Failed to mark all as read:', error)
      toast.custom((t) => (
        <EnterpriseToast
          title="Update Failed"
          message="Could not update notifications"
          type="error"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    }
  }

  const handleClearRead = async () => {
    try {
      const readIds = notifications
        .filter(n => n.read_at)
        .map(n => n.id)

      await supabase
        .from('secret_notifications')
        .delete()
        .in('id', readIds)

      setNotifications(prev => prev.filter(n => !n.read_at))
      toast.custom((t) => (
        <EnterpriseToast
          title="Cleared"
          message="Read notifications have been cleared"
          type="success"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    } catch (error) {
      console.error('Failed to clear notifications:', error)
      toast.custom((t) => (
        <EnterpriseToast
          title="Clear Failed"
          message="Could not clear notifications. Please try again"
          type="error"
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    }
  }

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'event_announcement':
        return <Bell className="text-secret-primary" size={20} />
      case 'order_confirmation':
      case 'order_status':
        return <CheckCheck className="text-green-500" size={20} />
      case 'transfer_received':
        return <MessageSquare className="text-blue-500" size={20} />
      default:
        return <Mail className="text-secret-text-secondary" size={20} />
    }
  }

  const getChannelBadges = (channels: Notification['channels']) => {
    return channels.map(channel => (
      <span
        key={channel}
        className="text-xs px-2 py-1 rounded bg-secret-bg-secondary text-secret-text-tertiary uppercase"
      >
        {channel}
      </span>
    ))
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  }

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read_at)
    : notifications

  const unreadCount = notifications.filter(n => !n.read_at).length

  if (isLoading) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen-safe bg-background">
      {/* Header */}
      <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-secret-border z-10 pt-safe">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-secret-text-secondary mt-1">
                  {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {notifications.length > 0 && (
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-sm text-secret-primary hover:text-secret-primary-hover transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                {notifications.some(n => n.read_at) && (
                  <button
                    onClick={handleClearRead}
                    className="text-sm text-secret-text-secondary hover:text-white transition-colors flex items-center space-x-1"
                  >
                    <Trash2 size={14} />
                    <span>Clear read</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex space-x-4">
            <button
              onClick={() => setFilter('all')}
              className={`pb-2 px-1 text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'text-white border-b-2 border-secret-primary'
                  : 'text-secret-text-secondary hover:text-white'
              }`}
            >
              All ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`pb-2 px-1 text-sm font-medium transition-colors ${
                filter === 'unread'
                  ? 'text-white border-b-2 border-secret-primary'
                  : 'text-secret-text-secondary hover:text-white'
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="container mx-auto px-4 py-6">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell size={48} className="mx-auto text-secret-text-tertiary mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </h3>
            <p className="text-secret-text-secondary">
              {filter === 'unread'
                ? 'Check back later for updates on your orders and events'
                : "We'll notify you about orders, events, and updates here"}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map(notification => (
              <div
                key={notification.id}
                className={`bg-secret-bg-secondary rounded-lg p-4 transition-all ${
                  !notification.read_at
                    ? 'border-l-4 border-secret-primary'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-white">{notification.title}</h4>
                      <div className="flex items-center space-x-2 ml-4">
                        {!notification.read_at && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="text-secret-text-secondary hover:text-white transition-colors"
                            title="Mark as read"
                          >
                            <Check size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    <p className="text-sm text-secret-text-secondary mb-3">
                      {notification.message}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getChannelBadges(notification.channels)}
                      </div>
                      <span className="text-xs text-secret-text-tertiary">
                        {formatTimestamp(notification.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage